import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { QueryKey } from '../../../constants/index';
import { PageRoute } from '../../../types/page.types';
import { DataRoomModalStore } from '../store/data-room-modal.store';
import { SidebarTreeStore } from '../store/sidebar-tree.store';
import { DataRoomViewer, useDataRoomViewer, useVaultAccess } from './use-recipient-view.hook';
import { useReachableSpaceKinds } from './use-space-kinds.hook';

// What the module reads for one role: a Contributor's Vault and workspaces, a Stakeholder's own
// folders. The Vault's keys do not say whose cut they hold, so they go with the role. A space's own
// detail goes too: it stays cached after Home is drawn again, for a space that may now be refused.
const ROLE_SCOPED_KEYS: ReadonlySet<unknown> = new Set([
  QueryKey.DATA_ROOM,
  QueryKey.DATA_ROOM_ACCESS,
  QueryKey.GET_HARDCODED_REPOSITORY,
  QueryKey.GET_HARDCODED_REPOSITORY_FILES,
  QueryKey.GET_SPACE_FOLDER_CONTENTS,
  QueryKey.GET_SPACE_NAVIGATION_FOLDERS,
  QueryKey.GET_SPACE_SEARCH,
  QueryKey.GET_SPACES_HOME,
  QueryKey.GET_SPACES_NAVIGATION,
  QueryKey.GET_VAULT_FOLDER,
  QueryKey.GET_WORKSPACE,
  QueryKey.MY_DATA_ROOMS,
  QueryKey.MY_DATA_ROOMS_NAVIGATION,
]);

/**
 * Starts the module over when the person's role changes while it is open — a Contributor taken
 * down to Stakeholder, or the other way, or a Guest User's Internal Workspaces / Data Rooms toggle
 * flipped by an admin, which the socket delivers as a refetched session: Home again, the tree
 * folded, any open modal closed, and every role-scoped read fetched afresh, so neither the open
 * folder nor the old role's cached tree is asked of routes that now refuse it. The spaces reads
 * keep a stale time, so a refetched session alone would leave them as they were until a reload.
 * The first role the session gives is where the visit starts, not a change.
 */
export const useResetOnViewerRoleChange = () => {
  const viewer = useDataRoomViewer();
  const vaultAccess = useVaultAccess();
  // Which kinds the spaces feed and the tree hold — a Guest Auditor's toggles included.
  const spaceKinds = useReachableSpaceKinds();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const resetTree = SidebarTreeStore((state) => state.reset);
  const roleRef = useRef<null | string>(null);
  const role =
    viewer === DataRoomViewer.UNRESOLVED
      ? null
      : `${viewer}:${vaultAccess}:${spaceKinds.join(',')}`;

  useEffect(() => {
    if (!role) return;

    const previous = roleRef.current;
    roleRef.current = role;

    if (!previous || previous === role) return;

    resetTree();

    // A settings, access or upload modal is about one space, which may be one they lost.
    const modal = DataRoomModalStore.getState();

    if (modal.isOpen) modal.handleDismiss();

    void queryClient.resetQueries({
      predicate: ({ queryKey: [key] }) => ROLE_SCOPED_KEYS.has(key),
    });

    // Activity is the team's tab; Home is where either role starts.
    if (pathname !== PageRoute.DATA_ROOM_WORKSPACES) {
      navigate(PageRoute.DATA_ROOM_WORKSPACES, { replace: true });
    }
  }, [navigate, pathname, queryClient, resetTree, role]);
};
